import React, {useState, useRef, useContext} from "react";
import "react-image-crop/dist/ReactCrop.css";
import PageHeaderImgCrop from "./PageHeaderImgCrop";
import Button from "@common/Button";
import {Tour, Progress} from "antd";
import {QuestionCircleOutlined} from "@ant-design/icons";
import axios from "axios";
import {ContentStylesDemoSubsection} from "../ContentStylesHelper";
import {ClassStylesEditorContext} from "../ContentStylesEditor";

const EDIT_TYPES = {
    BLOCK: "ch-page-header",
}

export const pageHeaderStylesRegistry = {
    [EDIT_TYPES.BLOCK]: {title: 'Page header'},
}

export const PageHeaderStyles = props => {
    const {updateCallback} = props;
    const {headerImage, workspace} = useContext(ClassStylesEditorContext);
    const [uploadProgress, setUploadProgress] = useState(null);
    const headerImageRef = useRef(null);
    const inputRef = useRef(null);

    // Tour variables
    const [open, setOpen] = useState(false);
    const uploadBtnRef = useRef(null);
    const cropBtnRef = useRef(null);
    const steps = [
        {
            title: 'Upload background image',
            placement: "right",
            cover: (
                <img
                    src="/assets/images/contentStylesEditor/contentStylesEditor-banner-upload.gif"
                    alt="Upload guide"
                    style={{
                        width: '100%',
                        objectFit: 'cover',
                    }}
                />
            ),
            description: (
                <>
                    <p>Click the <strong>Replace/Upload Master Image</strong> button to choose a high-resolution background.</p>
                </>
            ),
            target: () => uploadBtnRef.current,
        }, {
            title: 'Crop image & Save',
            placement: "right",
            cover: (
                <img
                    src="/assets/images/contentStylesEditor/contentStylesEditor-banner-crop.gif"
                    alt="Upload guide"
                    style={{
                        width: '100%',
                        objectFit: 'cover',
                    }}
                />
            ),
            description: (
                <>
                    <p>Click <strong>Adjust Position & Crop</strong> to focus on a area.</p>
                    <p className="margin-t--xs ch-text-small">
                        <em>Tip: Adjust crop, click <strong>Update</strong>, then click <strong>Save</strong>.</em>
                    </p>
                </>
            ),
            target: () => cropBtnRef.current,
        },
    ];

    const handleUploadImage = () => {
        const file = inputRef.current.files[0];
        if (!file) return;

        if (confirm("Are you sure you want to upload a new image? This will replace the current image.")) {
            uploadImage();
        } else {
            if(inputRef.current) inputRef.current.value = null;
        }
    }

    const uploadImage = async () => {
        const file = inputRef.current.files[0];
        if (!file) return;

        setUploadProgress(0);
        try {
            const url = '/web/ui/contentStyles/uploadHeaderImage';
            const formData = new FormData();
            formData.append('file', file);
            const config = {
                headers: {'Content-Type': 'multipart/form-data'},
                onUploadProgress: (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percent);
                }
            };
            const response = await axios.post(url, formData, config);
            updateCallback({
                headerImage: response.data
            });
        } catch (error) {
            console.error("Upload failed:", error);
        } finally {
            setUploadProgress(null);
        }
    };

    const handleCropImage = () => {
        headerImageRef.current = headerImage;

        updateCallback({
            workspace: {
                buttons:
                    <Button
                        type="primary"
                        onClick={() => handleSaveEditImage()}
                    >
                        Update
                    </Button>,
                output:
                    <PageHeaderImgCrop
                        imageSrc={headerImageRef.current?.masterUri}
                        imageCrop={headerImageRef.current?.crop}
                        minHeight={headerImageRef.current?.minHeight}
                        minWidth={headerImageRef.current?.minWidth}
                        updateCropCallback={handleUpdateCrop}
                    />
            }
        });
    }

    const handleUpdateCrop = (crop, previewImage) => {
        headerImageRef.current = {...headerImageRef.current, crop, previewImage: previewImage};
    }

    const handleSaveEditImage = () => {
        updateCallback({
            headerImage: headerImageRef.current,
            workspace: null
        }, true);
    }

    const isUploading = uploadProgress !== null;
    const hasImage = !!headerImage?.masterUri;

    return (
        <>
            <div className="flexSpaceBetween flexAlignBaseline">
                <h5>Banner image</h5>
                <a
                    role="button"
                    tabIndex={0}
                    className="ch-text-small pointer"
                    style={{color: 'var(--styles-edit-colour-RGB)'}}
                    onClick={() => setOpen(true)}
                >
                    <QuestionCircleOutlined style={{fontSize: '12px'}} /> Help
                </a>
            </div>
            <input
                style={{display: 'none'}}
                ref={inputRef}
                type='file'
                accept='image/*'
                onChange={handleUploadImage}
            />
            <Button
                ref={uploadBtnRef}
                className="margin-t--s"
                type="outline"
                disabled={workspace != null || isUploading}
                onClick={() => inputRef.current?.click()}
            >
                {hasImage ? "Replace Master Image" : "Upload Master Image"}
            </Button>
            {uploadProgress !== null && (
                <Progress percent={uploadProgress ?? 0} status="active" showInfo={false} />
            )}
            <Button
                ref={cropBtnRef}
                className="margin-t--s"
                type="outline"
                disabled={workspace != null || isUploading || !hasImage}
                onClick={handleCropImage}
            >
                Adjust Position & Crop
            </Button>
            <div className="margin-t--m padding-all--s bg-subtle border-radius-s">
                <div className="ch-text-small">
                    <ul>
                        <li>We recommend using a high-resolution landscape image (min 1920px width).</li>
                        <li>Optimized images will be generated automatically for the final versions.</li>
                    </ul>
                </div>
            </div>
            <Tour
                open={open}
                onClose={() => setOpen(false)}
                steps={steps}
                styles={{
                    section: {
                        width: '500px',
                    },
                    footer: {
                        display: 'flex',
                        justifyContent: 'flex-end',
                    },
                    actions: {
                        display: 'flex',
                        gap: '8px',
                        marginLeft: 'auto',
                    }
                }}
            />
        </>
    )
}

export const PageHeaderDemo = props => {
    const {headerImage, demoEvents} = props;
    const displaySrc = headerImage?.previewImage ?? headerImage?.uri ?? props.headerImageSrc;

    return (
        <ContentStylesDemoSubsection>
            <div
                className={EDIT_TYPES.BLOCK}
                {...demoEvents(EDIT_TYPES.BLOCK)}
                style={{
                    backgroundImage: `url(${displaySrc})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center center',
                }}
            />
        </ContentStylesDemoSubsection>
    );
};